import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GoogleAnalyticsEvents } from '../models/google-analytics-events.enum';

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private isInitialized = false;
  private initializationFailed = false;

  /**
   * Initialize Google Analytics synchronously
   * Returns immediately, script loads in background
   */
  initialize(): void {
    const gaId = environment.googleAnalyticsId;

    if (!gaId || gaId.trim() === '' || gaId.trim() === '${GOOGLE_ANALYTICS_ID}') {
      this.setupNoOpGtag();
      return;
    }

    // Set up gtag immediately (synchronously)
    window.dataLayer = window.dataLayer || [];

    function gtag(...args: any[]) {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }

    // Load the script asynchronously (non-blocking)
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(gtagScript);

    gtagScript.onload = () => {
      // Script is loaded, gtag is already available
    };

    gtagScript.onerror = () => {
      this.initializationFailed = true;
    };

    // Initialize gtag immediately
    gtag('js', new Date());
    gtag('config', gaId);

    window.gtag = gtag;

    this.isInitialized = true;
  }

  /**
   * Set up a no-op gtag function when GA is not available
   */
  private setupNoOpGtag(): void {
    window.gtag = (...args: any[]) => {
      console.log('🚫 gtag called but GA is not initialized:', args);
    };
  }

  /**
   * Track a page view with custom dimensions
   */
  trackPageViewWithCustomDimensions(
    pageTitle: string,
    pagePath: string,
    customDimensions?: Record<string, any>,
  ): void {
    if (!window.gtag) {
      return;
    }

    if (!this.isInitialized) {
      return;
    }

    const params: any = {
      page_title: pageTitle,
      page_path: pagePath,
    };

    // Add custom dimensions if provided
    if (customDimensions) {
      Object.assign(params, customDimensions);
    }

    window.gtag('config', environment.googleAnalyticsId, params);
  }

  /**
   * Track a custom event with enhanced parameters
   */
  trackEventWithDimensions(
    eventName: GoogleAnalyticsEvents,
    parameters?: Record<string, any>,
  ): void {
    if (!window.gtag) {
      return;
    }

    if (!this.isInitialized) {
      return;
    }

    window.gtag('event', eventName, parameters || {});
  }

  /**
   * Track page view (existing method kept for backward compatibility)
   */
  trackPageView(pageTitle: string, pagePath: string): void {
    if (!window.gtag) {
      return;
    }

    if (!this.isInitialized) {
      return;
    }

    const params = {
      page_title: pageTitle,
      page_path: pagePath,
    };

    window.gtag('config', environment.googleAnalyticsId, params);
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, parameters?: any): void {
    if (!window.gtag) {
      return;
    }

    if (!this.isInitialized) {
      return;
    }

    window.gtag('event', eventName, parameters);
  }

  /**
   * Check if Google Analytics is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Check if Google Analytics initialization failed
   */
  get initFailed(): boolean {
    return this.initializationFailed;
  }

  /**
   * Get current GA status for debugging
   */
  getStatus(): { initialized: boolean; initFailed: boolean; gtagAvailable: boolean } {
    const status = {
      initialized: this.isInitialized,
      initFailed: this.initializationFailed,
      gtagAvailable: typeof window.gtag === 'function',
    };
    return status;
  }
}
